'use client';

import { c as _c } from "react/compiler-runtime";
import type { IconButtonProps, MenuItemProps, DividerProps, PopperPlacementType } from '@mui/material';
import { ClickAwayListener, Fade, Paper, Popper, Divider, IconButton, MenuItem, MenuList, Stack } from '@mui/material';
import classnames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import { Icon } from '@/components/shared/icon';
import { Link } from '@/components/shared/link';
import { Loader } from '@/components/shared/loader';
import { GroupTitle } from './groupTitle';
import { AuthorizationTooltip } from '../tooltips/authorizationTooltip';
type IsDisabledResult = boolean | {
  disabled: boolean;
  tooltip?: string;
};
export interface OptionMenuItemType {
  id: string;
  groupHeader?: boolean;
  divider?: boolean;
  href?: string;
  dividerProps?: DividerProps;
  icon?: ReactNode;
  text?: ReactNode;
  rightContent?: ReactNode;
  nonInteractive?: boolean;
  menuItemProps?: MenuItemProps;
  predicate?: () => boolean;
  isDisabled?: () => IsDisabledResult | Promise<IsDisabledResult>;
  isAuthorized?: boolean;
  isFeatureSupported?: boolean;
  subMenu?: OptionType[];
  disabledTooltip?: string;
}
export type OptionType = string | OptionMenuItemType;
interface OptionsMenuType {
  icon?: ReactNode;
  iconClassName?: string;
  options: OptionType[];
  placement?: PopperPlacementType;
  iconButtonProps?: IconButtonProps;
  onToggle?: (open: boolean) => void;
  onOptionSelected?: (option: OptionType) => void;
  Component?: React.ElementType;
  componentProps?: Record<string, unknown>;
  popperClassName?: string;
}
const InnerMenu = props => {
  const $ = _c(30);
  const {
    onOptionSelected,
    closeMenu,
    open,
    options,
    anchorEl,
    placement,
    popperClassName
  } = props;
  const [subMenuOpen, setSubMenuOpen] = useState();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [disabledStates, setDisabledStates] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [loadingIds, setLoadingIds] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== open || $[3] !== options) {
    t2 = () => {
      if (!open) {
        return;
      }
      const optionsWithDynamicDisabled = options.filter(_temp);
      if (optionsWithDynamicDisabled.length === 0) {
        return;
      }
      setLoadingIds(new Set(optionsWithDynamicDisabled.map(_temp2)));
      Promise.all(optionsWithDynamicDisabled.map(async option => {
        const raw = await option.isDisabled();
        const resolved = typeof raw === "boolean" ? {
          disabled: raw
        } : raw;
        setDisabledStates(prev => ({
          ...prev,
          [option.id]: resolved
        }));
        setLoadingIds(prev_0 => {
          const next = new Set(prev_0);
          next.delete(option.id);
          return next;
        });
      }));
    };
    t3 = [open, options];
    $[2] = open;
    $[3] = options;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== anchorEl || $[7] !== closeMenu) {
    t4 = event => {
      if (anchorEl?.contains(event.target as HTMLElement)) {
        return;
      }
      setSubMenuOpen(undefined);
      closeMenu();
    };
    $[6] = anchorEl;
    $[7] = closeMenu;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleClose = t4;
  let t5;
  if ($[9] !== handleClose || $[10] !== onOptionSelected) {
    t5 = (option_0, e) => {
      if (onOptionSelected) {
        onOptionSelected(option_0);
      }
      handleClose(e);
    };
    $[9] = handleClose;
    $[10] = onOptionSelected;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleSelect = t5;
  let t6;
  if ($[12] !== options) {
    t6 = options.filter(_temp3);
    $[12] = options;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const filteredOptions = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      zIndex: 5
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== disabledStates || $[16] !== filteredOptions || $[17] !== handleClose || $[18] !== handleSelect || $[19] !== loadingIds || $[20] !== open || $[21] !== props || $[22] !== subMenuOpen) {
    t8 = t9 => {
      const {
        TransitionProps
      } = t9;
      return <Fade {...TransitionProps}><Paper sx={{
          boxShadow: "var(--mui-customShadows-lg)"
        }}><ClickAwayListener onClickAway={handleClose}><MenuList autoFocusItem={open} onClick={props.enablePropagation ? undefined : _temp4}>{filteredOptions.map((option_1, index) => {
                if (typeof option_1 === "string") {
                  return <MenuItem key={option_1} data-testid={option_1} sx={{
                    minWidth: "150px"
                  }} onClick={e_1 => {
                    handleSelect(option_1, e_1);
                  }}>{option_1}</MenuItem>;
                } else {
                  if ("divider" in option_1) {
                    const previousOption = filteredOptions[index - 1];
                    if (index === 0 || index === filteredOptions.length - 1 || typeof previousOption !== "string" && "divider" in previousOption) {
                      return null;
                    }
                    if (!option_1.divider) {
                      return null;
                    }
                    return <Divider key={option_1.id} {...option_1.dividerProps} className="my-[8px]" />;
                  } else {
                    if ("groupHeader" in option_1) {
                      return <GroupTitle key={option_1.id} title={option_1.text} />;
                    } else {
                      if (option_1.nonInteractive) {
                        return <Stack key={option_1.id} data-testid={option_1.id} onClick={_temp5} sx={{
                          padding: "6px 16px",
                          cursor: "default"
                        }}><Option icon={option_1.icon} text={option_1.text} rightContent={option_1.rightContent} /></Stack>;
                      } else {
                        return <SubMenuInnerOption key={option_1.id} option={option_1} subMenuOpen={subMenuOpen} index={index} setSubMenuOpen={setSubMenuOpen} handleSelect={handleSelect} props={props} dynamicDisabledState={disabledStates[option_1.id]} isLoadingDisabled={loadingIds.has(option_1.id)} />;
                      }
                    }
                  }
                }
              })}</MenuList></ClickAwayListener></Paper></Fade>;
    };
    $[15] = disabledStates;
    $[16] = filteredOptions;
    $[17] = handleClose;
    $[18] = handleSelect;
    $[19] = loadingIds;
    $[20] = open;
    $[21] = props;
    $[22] = subMenuOpen;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== anchorEl || $[25] !== open || $[26] !== placement || $[27] !== popperClassName || $[28] !== t8) {
    t9 = <Popper open={open} anchorEl={anchorEl} placement={placement} transition={true} disablePortal={true} className={popperClassName} sx={t7}>{t8}</Popper>;
    $[24] = anchorEl;
    $[25] = open;
    $[26] = placement;
    $[27] = popperClassName;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};
const SubMenuInnerOption = t0 => {
  const $ = _c(41);
  const {
    option,
    subMenuOpen,
    index,
    setSubMenuOpen,
    handleSelect,
    props,
    dynamicDisabledState,
    isLoadingDisabled
  } = t0;
  const [anchorEl, setAnchorEl] = useState(null);
  const isDisabled = option.menuItemProps?.disabled || isLoadingDisabled || dynamicDisabledState?.disabled;
  const tooltipTitle = isDisabled ? dynamicDisabledState?.tooltip ?? option.disabledTooltip : undefined;
  let t1;
  if ($[0] !== option.href) {
    t1 = option.href ? {
      component: Link,
      href: option.href
    } : {};
    $[0] = option.href;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const linkProps = t1;
  let t2;
  if ($[2] !== index || $[3] !== option.menuItemProps || $[4] !== setSubMenuOpen) {
    t2 = e => {
      setSubMenuOpen(index);
      option.menuItemProps?.onMouseEnter?.(e);
    };
    $[2] = index;
    $[3] = option.menuItemProps;
    $[4] = setSubMenuOpen;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== option.menuItemProps || $[7] !== setSubMenuOpen) {
    t3 = e_0 => {
      setSubMenuOpen(undefined);
      option.menuItemProps?.onMouseLeave?.(e_0);
    };
    $[6] = option.menuItemProps;
    $[7] = setSubMenuOpen;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== handleSelect || $[10] !== option) {
    t4 = e_1 => {
      handleSelect(option, e_1);
      option.menuItemProps?.onClick?.(e_1);
    };
    $[9] = handleSelect;
    $[10] = option;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = !!option.subMenu;
  let t6;
  if ($[12] !== isLoadingDisabled || $[13] !== option.icon || $[14] !== option.rightContent || $[15] !== option.text || $[16] !== t5) {
    t6 = <Option icon={option.icon} text={option.text} rightContent={option.rightContent} isSubMenu={t5} isLoadingDisabled={isLoadingDisabled} />;
    $[12] = isLoadingDisabled;
    $[13] = option.icon;
    $[14] = option.rightContent;
    $[15] = option.text;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== anchorEl || $[19] !== index || $[20] !== option.subMenu || $[21] !== props || $[22] !== setSubMenuOpen || $[23] !== subMenuOpen) {
    t7 = option.subMenu && <InnerMenu {...props} anchorEl={anchorEl} options={option.subMenu} placement="right-start" open={subMenuOpen === index} closeMenu={() => setSubMenuOpen(undefined)} enablePropagation={true} />;
    $[18] = anchorEl;
    $[19] = index;
    $[20] = option.subMenu;
    $[21] = props;
    $[22] = setSubMenuOpen;
    $[23] = subMenuOpen;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== isDisabled || $[26] !== linkProps || $[27] !== option.id || $[28] !== option.menuItemProps || $[29] !== t2 || $[30] !== t3 || $[31] !== t4 || $[32] !== t6 || $[33] !== t7) {
    t8 = <MenuItem {...option.menuItemProps} {...linkProps} disabled={isDisabled} ref={setAnchorEl} onMouseEnter={t2} onMouseLeave={t3} onClick={t4} data-testid={option.id}>{t6}{t7}</MenuItem>;
    $[25] = isDisabled;
    $[26] = linkProps;
    $[27] = option.id;
    $[28] = option.menuItemProps;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== option.id || $[36] !== option.isAuthorized || $[37] !== option.isFeatureSupported || $[38] !== t8 || $[39] !== tooltipTitle) {
    t9 = <AuthorizationTooltip isAuthorized={option.isAuthorized} isFeatureSupported={option.isFeatureSupported} title={tooltipTitle} key={option.id}>{t8}</AuthorizationTooltip>;
    $[35] = option.id;
    $[36] = option.isAuthorized;
    $[37] = option.isFeatureSupported;
    $[38] = t8;
    $[39] = tooltipTitle;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  return t9;
};
const OptionMenu = props => {
  const $ = _c(26);
  const {
    icon: t0,
    iconClassName,
    iconButtonProps,
    onToggle,
    Component,
    componentProps,
    placement: t1
  } = props;
  const icon = t0 === undefined ? "material-symbols-more-horiz" : t0;
  const placement = t1 === undefined ? "bottom-end" : t1;
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  let t2;
  if ($[0] !== onToggle || $[1] !== open) {
    t2 = event => {
      event.stopPropagation();
      setOpen(_temp6);
      onToggle?.(!open);
    };
    $[0] = onToggle;
    $[1] = open;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleToggle = t2;
  if (props.options.length === 0 || props.options.every(_temp7)) {
    return null;
  }
  const ButtonComponent = Component as React.ComponentType<Record<string, unknown> & {
    ref?: React.Ref<HTMLElement>;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
  }>;
  let t3;
  if ($[3] !== ButtonComponent || $[4] !== Component || $[5] !== componentProps || $[6] !== handleToggle) {
    t3 = Component && <ButtonComponent {...componentProps} data-testid="option-menu-button" ref={setAnchorEl} onClick={handleToggle} />;
    $[3] = ButtonComponent;
    $[4] = Component;
    $[5] = componentProps;
    $[6] = handleToggle;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== Component || $[9] !== handleToggle || $[10] !== icon || $[11] !== iconButtonProps || $[12] !== iconClassName) {
    t4 = !Component && <IconButton disableRipple={true} ref={setAnchorEl} size="small" data-testid="option-menu-button" onClick={handleToggle} {...iconButtonProps}>{typeof icon === "string" ? <i className={classnames(icon, "text-(--mui-palette-text-primary)", iconClassName)} /> : icon}</IconButton>;
    $[8] = Component;
    $[9] = handleToggle;
    $[10] = icon;
    $[11] = iconButtonProps;
    $[12] = iconClassName;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== onToggle) {
    t5 = () => {
      setOpen(false);
      onToggle?.(false);
    };
    $[14] = onToggle;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== anchorEl || $[17] !== open || $[18] !== placement || $[19] !== props || $[20] !== t5) {
    t6 = <InnerMenu {...props} placement={placement} open={open} closeMenu={t5} anchorEl={anchorEl} />;
    $[16] = anchorEl;
    $[17] = open;
    $[18] = placement;
    $[19] = props;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t3 || $[23] !== t4 || $[24] !== t6) {
    t7 = <>{t3}{t4}{t6}</>;
    $[22] = t3;
    $[23] = t4;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
};
export const menuItemStyles = (isSelected: boolean) => {
  return {
    height: '36px',
    padding: '4px 16px 4px 16px',
    borderRadius: 0,
    ...(isSelected ? {
      backgroundColor: 'var(--mui-palette-primary-main)',
      color: 'var(--mui-palette-background-default)'
    } : {}),
    '&:hover': {
      backgroundColor: isSelected ? 'var(--mui-palette-primary-main)' : 'var(--mui-palette-primary-dark)'
    }
  };
};
export { OptionMenu };
const Option = t0 => {
  const $ = _c(17);
  const {
    icon,
    text,
    rightContent,
    isSubMenu,
    isLoadingDisabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      gap: "4px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      alignItems: "center",
      gap: "8px",
      marginRight: "8px"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== icon) {
    t3 = (typeof icon === "string" ? <Icon iconClass={icon} sx={{
      "& i": {
        color: "currentColor"
      }
    }} /> : icon) || null;
    $[2] = icon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3 || $[5] !== text) {
    t4 = <Stack direction="row" sx={t2}>{t3}{text}</Stack>;
    $[4] = t3;
    $[5] = text;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isLoadingDisabled) {
    t5 = isLoadingDisabled && <Loader size="small" color="inherit" />;
    $[7] = isLoadingDisabled;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isLoadingDisabled || $[10] !== isSubMenu) {
    t6 = isSubMenu && !isLoadingDisabled && <Icon iconClass="material-symbols-chevron-right-rounded" sx={{
      padding: "0px",
      "& i": {
        margin: "0px",
        color: "currentColor"
      }
    }} />;
    $[9] = isLoadingDisabled;
    $[10] = isSubMenu;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== rightContent || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <Stack direction="row" sx={t1}>{t4}{rightContent}{t5}{t6}</Stack>;
    $[12] = rightContent;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
function _temp(o) {
  return typeof o === "object" && "isDisabled" in o && typeof o.isDisabled === "function";
}
function _temp2(o_0) {
  return o_0.id;
}
function _temp3(o_1) {
  return typeof o_1 === "object" && "predicate" in o_1 ? o_1.predicate?.() : true;
}
function _temp4(e_0) {
  return e_0.stopPropagation();
}
function _temp5(e_2) {
  return e_2.stopPropagation();
}
function _temp6(prevOpen) {
  return !prevOpen;
}
function _temp7(obj) {
  return typeof obj === "object" && ("predicate" in obj && !obj.predicate?.() || obj.divider);
}